import { SphereIconButton } from "@components/common/sphere-icon-button";
import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { LabelWithHelp } from "@components/common/label-with-help";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { assert } from "@faro-lotv/foundation";
import { Box, FormControl } from "@mui/material";
import { useUserContext } from "@pages/account-settings/user-provider";
import { EmailUtils } from "@stellar/utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { updatedEmailSelector } from "@store/user/user-selector";
import { setUpdatedEmail } from "@store/user/user-slice";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { HTMLInputTypeAttribute, useState } from "react";
import {
  Dialog,
  neutral,
  VisibleIcon,
  NonVisibleIcon,
  useToast,
} from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { faroCoreApiClient } from "@api/faro-core-api-client";

/** Defines the possible input types for a text field */
type InputType = Extract<HTMLInputTypeAttribute, "password" | "text">;

/**
 * Component that displays an text input to change the user's email,
 * once the user accepts the changes, it shows a dialog to
 * prompt for the user's password.
 */
export function AccountChangeEmailDialog(): JSX.Element {
  const { user } = useUserContext();
  assert(user, "User has to be defined to change email");
  const userEmail = user.email;

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const updatedEmail = useAppSelector(updatedEmailSelector);
  const [newDisplayEmail, setNewDisplayEmail] = useState<string>(
    updatedEmail ?? userEmail
  );
  const [isChangingEmail, setIsChangingEmail] = useState<boolean>(false);
  /** Alternate between password and text to show or hide the user's password in the text field  */
  const [passwordInputType, setPasswordInputType] =
    useState<InputType>("password");
  const [userPassword, setUserPassword] = useState<string>("");
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();

  /**
   * Opens the dialog and sets the initial value for the email field.
   *
   * @param newEmail New email to set as initial value for the email field.
   * @returns The new email if the user confirms the change, or the current email if the user
   * cancels the change.
   */
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function openDialog(newEmail: string): Promise<string> {
    if (newEmail.toLowerCase() === userEmail.toLowerCase()) {
      // Do not show dialog if the email didn't change
      return userEmail;
    }
    setIsDialogOpen(true);
    setNewDisplayEmail(newEmail);
    return newEmail;
  }

  /** Closes the dialog and resets the email field to the user's email */
  function closeDialog(): void {
    setIsDialogOpen(false);
    setUserPassword("");
    setIsChangingEmail(false);
  }

  /** Triggered when user clicks on cancel, closes the dialog. */
  function onCancel(): void {
    closeDialog();
    setNewDisplayEmail(userEmail ?? "");
  }

  /**
   * Triggered when user clicks on confirm to change email, calls the updateCurrentUserEmail
   * action to update the user's email and closes the dialog at the end.
   */
  async function onConfirmEmail(): Promise<void> {
    setIsChangingEmail(true);
    trackEvent({
      name: UserProfile.updateProfile,
      props: { attribute: "email" },
    });

    try {
      if (!newDisplayEmail) {
        throw new Error("Email is required");
      }
      await faroCoreApiClient.users.updateUserEmail({
        email: newDisplayEmail,
        password: userPassword,
      });

      closeDialog();
      openToast({
        title: "Request to change email successfully sent",
        message:
          "Please check your inbox to validate your new email." +
          "Your email won't be updated until you validate it.",
      });
      dispatch(setUpdatedEmail(newDisplayEmail));
      setNewDisplayEmail(newDisplayEmail);
    } catch (error) {
      setIsChangingEmail(false);
      handleErrorWithToast({
        id: `AccountChangeEmailDialog-${Date.now().toString()}`,
        title: "Could not change email",
        error,
      });
      // Do not close dialog, let user try again in case password was incorrect
    }
  }

  return (
    <>
      <Box component="div" width="100%">
        <FaroTextField
          initialValue={newDisplayEmail}
          onConfirmed={openDialog}
          isFullWidth={true}
          faroVariant="row"
          isReadOnly={false}
          minInputLength={1}
          validate={EmailUtils.isValidEmail}
        />
      </Box>

      <Dialog
        title={"Email address"}
        open={isDialogOpen}
        showSpinner={isChangingEmail}
        isConfirmDisabled={!userPassword.length}
        onCancel={onCancel}
        onConfirm={onConfirmEmail}
      >
        <Box
          component="div"
          sx={{
            fontSize: DEFAULT_INPUT_FONT_SIZE,
            color: neutral[800],
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          After confirming your password, you will receive a verification email
          with a link you can click on to finalize the change.
        </Box>

        <LabelWithHelp title="New email" />
        <FaroSimpleTextField
          disabled
          value={newDisplayEmail}
          fullWidth={true}
          size="small"
          InputProps={{
            sx: {
              mb: SPACE_ELEMENTS_OF_MODAL,
              backgroundColor: neutral[100],
              "& .MuiInputBase-input.Mui-disabled": {
                color: neutral[700],
                WebkitTextFillColor: neutral[700],
              },
            },
          }}
        />

        <LabelWithHelp title="Confirm your password" />
        <FormControl fullWidth={true}>
          <FaroSimpleTextField
            value={userPassword}
            onChange={(event) => setUserPassword(event.target.value)}
            fullWidth={true}
            size="small"
            type={passwordInputType}
            placeholder="Enter current password"
            onKeyUp={(event) => {
              if (event.key === "Enter" && userPassword.length) {
                // Send request if user presses enter and password is not empty
                void onConfirmEmail();
              }
            }}
            InputProps={{
              endAdornment: (
                <SphereIconButton
                  component={
                    passwordInputType === "password"
                      ? NonVisibleIcon
                      : VisibleIcon
                  }
                  onClick={() =>
                    setPasswordInputType(
                      passwordInputType === "password" ? "text" : "password"
                    )
                  }
                  buttonSize="24px"
                  iconSize="18px"
                />
              ),
              sx: {
                "& .MuiInputBase-input.Mui-disabled": {
                  color: neutral[800],
                },
              },
            }}
          />
        </FormControl>
      </Dialog>
    </>
  );
}
